import { useEffect, useState } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useCopilot } from '../hooks/useCopilot'
import { Phase } from '../state/types'
import { EmptyState } from './EmptyState'
import { OutcomeBar } from './OutcomeBar'
import { SessionBody } from './SessionBody'
import { SessionSwitcher } from './SessionSwitcher'

const WIDE_KEY = 'copilot.panelWide'

function readWide(): boolean {
  // Wide is the default (the redesign is authored at ~760px); 420px is the fallback.
  // An explicit stored 'false' still wins, so a narrow preference survives a reload.
  try { return window.localStorage.getItem(WIDE_KEY) !== 'false' } catch { return true }
}

// Maps the run phase to the command-bar status pill tone (drives colour + pulse).
const PHASE_TONE: Record<Phase, 'working' | 'wait' | 'done' | 'error' | 'idle'> = {
  empty: 'idle',
  uploading: 'working',
  creating: 'working',
  queued: 'working',
  streaming: 'working',
  awaiting_confirmation: 'wait',
  awaiting_answer: 'wait',
  complete: 'done',
  error: 'error',
  interrupted: 'idle',
}

export function Panel() {
  const { t } = useTranslation()
  const { state, close } = useCopilot()
  const active = state.activeId ? state.sessions[state.activeId] : null
  const [showNew, setShowNew] = useState(false)
  // Panel width is a pure UI preference (not session state), so it lives here and persists across reloads.
  const [wide, setWide] = useState(readWide)

  // When startRun creates a session and activeId changes, clear the showNew flag
  useEffect(() => { setShowNew(false) }, [state.activeId])
  useEffect(() => { try { window.localStorage.setItem(WIDE_KEY, String(wide)) } catch { /* ignore */ } }, [wide])

  const showEmpty = !active || showNew

  return (
    <Offcanvas
      className={`copilot-panel${wide ? ' copilot-panel--wide' : ''}`}
      show={state.open}
      onHide={close}
      placement="end"
      backdrop={false}
      aria-labelledby="copilot-cmd-name"
      scroll
    >
      <Offcanvas.Header closeButton closeVariant="white">
        <div className="copilot-head">
          <div className="copilot-cmd">
            <span className="copilot-mark" aria-hidden="true">
              <i className="fa fa-stamp" />
            </span>
            <span className="copilot-id">
              <span className="copilot-id__name" id="copilot-cmd-name">{t('copilot.title')}</span>
              <span className="copilot-id__sub">{t('copilot.subtitle')}</span>
            </span>
            {active && !showEmpty && (
              <span className={`copilot-status copilot-status--${PHASE_TONE[active.phase]}`}>
                <span className="copilot-status__dot" />
                {t(`copilot.phase.${active.phase}`)}
              </span>
            )}
            <button
              type="button"
              className="copilot-expand"
              data-testid="copilot-expand"
              aria-pressed={wide}
              aria-label={t(wide ? 'copilot.collapse' : 'copilot.expand')}
              title={t(wide ? 'copilot.collapse' : 'copilot.expand')}
              onClick={() => setWide((w) => !w)}
            >
              <i className={`fa ${wide ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" />
            </button>
          </div>
          <SessionSwitcher onNew={() => setShowNew(true)} onSelect={() => setShowNew(false)} />
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {active && !showEmpty && <OutcomeBar session={active} />}
        <div data-testid="copilot-body" className="copilot-body">
          {showEmpty
            ? <EmptyState />
            : <SessionBody session={active!} />}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  )
}
